/**
 * FicheTabs — top-level view switcher for the fiche page.
 *
 * Three views live under the same URL (/fiche/:id), selected by the
 * `?view=` query parameter so users can deep-link / share a specific tab :
 *
 *   • Fiche        — the canonical analysis (sections 01 → 05)
 *   • Environnement — external data enrichments (DPE voisins, écoles,
 *                    commerces, transports, etc.)
 *   • Analyse IA    — Claude-generated insights (synthèse, red flags,
 *                    estimation, potentiel d'agrandissement, visuel)
 *
 * The tab state is lifted one level up (in Fiche.tsx) so the sheet header
 * stays identical across tabs — only the body swaps. The component itself
 * is purely presentational.
 */

import type { ReactNode } from "react";
import { Ic } from "./icons";

export type FicheView = "fiche" | "env" | "ai";

export const FICHE_VIEWS: FicheView[] = ["fiche", "env", "ai"];

type TabDef = { id: FicheView; label: string; icon: ReactNode };

const TABS: TabDef[] = [
  { id: "fiche", label: "Fiche", icon: <Ic.File size={14} /> },
  { id: "env", label: "Environnement", icon: <Ic.Compass size={14} /> },
  { id: "ai", label: "Analyse IA", icon: <Ic.Sparkle size={14} /> },
];

export function FicheTabs({
  active,
  onChange,
  className = "",
}: {
  active: FicheView;
  onChange: (next: FicheView) => void;
  className?: string;
}) {
  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full bg-ink-50 p-1 hairline ${className}`}
      role="tablist"
      aria-label="Vues de la fiche"
    >
      {TABS.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-medium transition ${
              isActive
                ? "bg-ink-900 text-lime-300 shadow-pop"
                : "text-ink-600 hover:text-ink-900"
            }`}
          >
            <span className={isActive ? "text-lime-300" : "text-ink-500"}>
              {t.icon}
            </span>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Parse the `view` query param into a `FicheView`, defaulting to "fiche".
 * Used by Fiche.tsx to initialise state from the URL.
 */
export function parseFicheView(search: string): FicheView {
  const params = new URLSearchParams(search);
  const v = params.get("view");
  if (v === "env" || v === "ai") return v;
  return "fiche";
}

/**
 * Serialise the view back to a URL (preserving other query params). Used
 * to `history.replaceState` when the user clicks a tab — we keep the URL
 * in sync so a refresh or a copy-paste share retains the tab.
 */
export function viewToQueryString(search: string, view: FicheView): string {
  const params = new URLSearchParams(search);
  if (view === "fiche") {
    params.delete("view");
  } else {
    params.set("view", view);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
